import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Search,
  Person,
  LocationOn,
  Phone,
  Email,
  CalendarToday,
  FilterList,
  MoreVert,
  Add,
  Edit,
  Delete,
  Message,
  Schedule,
  LocalHospital,
  Assignment,
  CheckCircle,
  Warning,
  Info,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePatientManagement } from '../hooks/usePatientManagement';
import { Patient } from '../services/patientManagementService';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`patient-tabpanel-${index}`}
      aria-labelledby={`patient-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PatientManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const {
    patients,
    loading,
    error,
    searchPatients,
    updatePatientStatus,
    removePatient,
  } = usePatientManagement();


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'completed': return 'info';
      case 'on_hold': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'pending': return <Warning />;
      case 'completed': return <CheckCircle />;
      case 'on_hold': return <Info />;
      default: return <Info />;
    }
  };

  const getFilteredPatients = () => {
    return patients;
  };

  const handleSearch = async () => {
    const statusMap = ['all', 'active', 'pending', 'on_hold'];
    const status = statusMap[tabValue];
    
    await searchPatients({
      q: searchTerm || undefined,
      status: status !== 'all' ? status : undefined
    });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, patient: Patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const handleDeletePatient = async () => {
    if (selectedPatient) {
      try {
        await removePatient(selectedPatient.id);
        setDeleteDialogOpen(false);
        handleMenuClose();
      } catch (err) {
        console.error('Failed to remove patient:', err);
      }
    }
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Trigger search with new tab value
    const statusMap = ['all', 'active', 'pending', 'on_hold'];
    const status = statusMap[newValue];
    
    await searchPatients({
      q: searchTerm || undefined,
      status: status !== 'all' ? status : undefined
    });
  };

  const renderPatientCard = (patient: Patient) => (
    <Grid item xs={12} sm={6} md={4} key={patient.id}>
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          '&:hover': {
            boxShadow: 3,
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                {patient.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" component="div">
                  {patient.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Age {patient.age} • {patient.location}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => handleMenuClick(e, patient)}
            >
              <MoreVert />
            </IconButton>
          </Box>

          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <Chip
                icon={getStatusIcon(patient.status)}
                label={patient.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(patient.status) as any}
                size="small"
                sx={{ mr: 1 }}
              />
              {patient.nextAppointment && (
                <Chip
                  icon={<CalendarToday />}
                  label="Next: Oct 22"
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Conditions:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {patient.conditions.slice(0, 2).map((condition, index) => (
                <Chip
                  key={index}
                  label={condition}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ))}
              {patient.conditions.length > 2 && (
                <Chip
                  label={`+${patient.conditions.length - 2}`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Care Needs:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {patient.careNeeds.slice(0, 2).map((need, index) => (
                <Chip
                  key={index}
                  label={need}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
              {patient.careNeeds.length > 2 && (
                <Chip
                  label={`+${patient.careNeeds.length - 2}`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Last visit: {patient.lastVisit}
          </Typography>
        </CardContent>
        
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="outlined"
            startIcon={<Message />}
            onClick={() => navigate(`/patient/${patient.id}/messages`)}
            size="small"
          >
            Message
          </Button>
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            onClick={() => navigate(`/patient/${patient.id}/schedule`)}
            size="small"
          >
            Schedule
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const filteredPatients = getFilteredPatients();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Careseeker Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your active careseekers, track care plans, and monitor their progress.
        </Typography>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search careseekers by name, location, conditions, or care needs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              {loading ? 'Searching...' : 'Search Careseekers'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="patient status tabs"
          variant="fullWidth"
        >
          <Tab 
            label={
              <Badge badgeContent={patients.length} color="primary">
                All Careseekers
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={patients.filter(p => p.status === 'active').length} color="success">
                Active
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={patients.filter(p => p.status === 'pending').length} color="warning">
                Pending
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={patients.filter(p => p.status === 'on_hold').length} color="error">
                On Hold
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* Results */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredPatients.length > 0 ? (
        <Grid container spacing={3}>
          {filteredPatients.map(renderPatientCard)}
        </Grid>
      ) : (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No patients found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm 
              ? 'Try adjusting your search terms or browse all patients.'
              : 'No patients match the current filter criteria.'
            }
          </Typography>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => navigate(`/patient/${selectedPatient?.id}`)}>
          <Person sx={{ mr: 1 }} />
          View Profile
        </MenuItem>
        <MenuItem onClick={() => navigate(`/patient/${selectedPatient?.id}/edit`)}>
          <Edit sx={{ mr: 1 }} />
          Edit Careseeker
        </MenuItem>
        <MenuItem onClick={() => navigate(`/patient/${selectedPatient?.id}/notes`)}>
          <Assignment sx={{ mr: 1 }} />
          Add Notes
        </MenuItem>
        <MenuItem onClick={() => setDeleteDialogOpen(true)} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          Remove Careseeker
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Remove Careseeker</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedPatient?.name} from your careseeker list? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePatient} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PatientManagementPage;
